import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import {
  ArrowLeft,
  User,
  Mail,
  Phone,
  MapPin,
  Calendar,
  FileText,
  Clock,
  Euro,
  Star,
  Upload,
  Download,
  Trash2,
  Edit,
  Save,
  X,
  Plus,
  AlertTriangle,
  CheckCircle,
  Briefcase,
  Car,
  Leaf,
  Loader2,
  Wrench,
  GraduationCap,
  CalendarDays,
  ClipboardList,
  TrendingUp,
  TrendingDown,
  Camera,
  Shield,
  Building2,
  Tractor,
  StickyNote,
  Lock,
  UserPlus,
  Copy,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Composant pour gérer les accès du collaborateur
const AccesCollaborateurPanel = ({ collaborateurId }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [accesData, setAccesData] = useState(null);
  const [societesDisponibles, setSocietesDisponibles] = useState([]);
  const [modulesDisponibles, setModulesDisponibles] = useState({});
  
  const [societeDefaut, setSocieteDefaut] = useState('');
  const [societesAutorisees, setSocietesAutorisees] = useState([]);
  const [accesToutesSocietes, setAccesToutesSocietes] = useState(false);
  const [modulesInterdits, setModulesInterdits] = useState([]);
  const [roleGlobal, setRoleGlobal] = useState('collaborateur');

  useEffect(() => {
    const loadAcces = async () => {
      try {
        const response = await fetch(`${API_URL}/api/acces-collaborateurs/collaborateur/${collaborateurId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setAccesData(data.acces);
          setSocietesDisponibles(data.societes_disponibles || []);
          setModulesDisponibles(data.modules_disponibles || {});
          
          setSocieteDefaut(data.acces?.societe_defaut_id || '');
          setSocietesAutorisees(data.acces?.societes_autorisees || []);
          setAccesToutesSocietes(data.acces?.acces_toutes_societes || false);
          setModulesInterdits(data.acces?.modules_interdits || []);
          setRoleGlobal(data.acces?.role_global || 'collaborateur');
        }
      } catch (error) {
        console.error('Erreur chargement accès:', error);
      } finally {
        setLoading(false);
      }
    };
    
    if (collaborateurId) loadAcces();
  }, [collaborateurId, token]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/acces-collaborateurs/collaborateur/${collaborateurId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          collaborateur_id: collaborateurId,
          societe_defaut_id: societeDefaut || null,
          societes_autorisees: accesToutesSocietes ? [] : societesAutorisees,
          acces_toutes_societes: accesToutesSocietes,
          modules_autorises: [],
          modules_interdits: modulesInterdits,
          role_global: roleGlobal
        })
      });
      
      if (response.ok) {
        toast.success('Accès enregistrés avec succès');
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setSaving(false);
    }
  };

  const toggleSociete = (societeId) => {
    setSocietesAutorisees(prev => 
      prev.includes(societeId) 
        ? prev.filter(id => id !== societeId)
        : [...prev, societeId]
    );
  };

  const toggleModule = (moduleId) => {
    setModulesInterdits(prev => 
      prev.includes(moduleId)
        ? prev.filter(id => id !== moduleId)
        : [...prev, moduleId]
    );
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto"></div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Société par défaut */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-green-600" />
            Société par défaut à la connexion
          </CardTitle>
          <CardDescription>
            La société qui s'affichera automatiquement quand ce collaborateur se connecte
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Select value={societeDefaut} onValueChange={setSocieteDefaut}>
            <SelectTrigger>
              <SelectValue placeholder="Sélectionner une société" />
            </SelectTrigger>
            <SelectContent>
              {societesDisponibles.map(s => (
                <SelectItem key={s.id} value={s.id}>{s.nom}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Sociétés accessibles */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-600" />
            Sociétés accessibles
          </CardTitle>
          <CardDescription>
            Les sociétés auxquelles ce collaborateur peut accéder
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
            <div className="flex items-center gap-3">
              <Switch
                checked={accesToutesSocietes}
                onCheckedChange={setAccesToutesSocietes}
              />
              <div>
                <p className="font-medium">Accès à toutes les sociétés</p>
                <p className="text-sm text-muted-foreground">
                  Ex: secrétaire, dirigeant, comptable
                </p>
              </div>
            </div>
          </div>
          
          {!accesToutesSocietes && (
            <div className="grid grid-cols-2 gap-2">
              {societesDisponibles.map(s => (
                <div
                  key={s.id}
                  onClick={() => toggleSociete(s.id)}
                  className={`p-3 border rounded-lg cursor-pointer transition-all ${
                    societesAutorisees.includes(s.id)
                      ? 'border-green-500 bg-green-50'
                      : 'border-border hover:border-green-200'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Checkbox checked={societesAutorisees.includes(s.id)} />
                    <span className="font-medium">{s.nom}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modules interdits */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-red-600" />
            Modules interdits
          </CardTitle>
          <CardDescription>
            Les modules que ce collaborateur ne peut PAS utiliser (tous les autres sont accessibles)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {Object.entries(modulesDisponibles).map(([moduleId, info]) => (
              <div
                key={moduleId}
                onClick={() => toggleModule(moduleId)}
                className={`p-3 border rounded-lg cursor-pointer transition-all ${
                  modulesInterdits.includes(moduleId)
                    ? 'border-red-500 bg-red-50'
                    : 'border-border hover:border-red-200'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Checkbox checked={modulesInterdits.includes(moduleId)} />
                  <div>
                    <p className="font-medium text-sm">{info.nom}</p>
                    <p className="text-xs text-muted-foreground">{info.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Rôle global */}
      <Card>
        <CardHeader>
          <CardTitle>Rôle global</CardTitle>
        </CardHeader>
        <CardContent>
          <Select value={roleGlobal} onValueChange={setRoleGlobal}>
            <SelectTrigger className="w-64">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="collaborateur">Collaborateur</SelectItem>
              <SelectItem value="responsable">Responsable</SelectItem>
              <SelectItem value="admin">Administrateur</SelectItem>
              <SelectItem value="dirigeant">Dirigeant</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Bouton sauvegarder */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} className="w-48">
          {saving ? (
            <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Enregistrer les accès
        </Button>
      </div>
    </div>
  );
};

// Types de contrat
const TYPES_CONTRAT = {
  cdi: 'CDI',
  cdd: 'CDD',
  cdd_saisonnier: 'CDD Saisonnier',
  apprentissage: 'Apprentissage',
  stage: 'Stage',
  freelance: 'Freelance',
  interimaire: 'Intérimaire',
  prestataire: 'Prestataire',
  eta: 'ETA',
};

// Statuts de contrat
const STATUTS_CONTRAT = {
  actif: { label: 'Actif', color: 'bg-green-100 text-green-800' },
  suspendu: { label: 'Suspendu', color: 'bg-yellow-100 text-yellow-800' },
  termine: { label: 'Terminé', color: 'bg-gray-100 text-gray-800' },
  rupture: { label: 'Rupture', color: 'bg-red-100 text-red-800' },
  renouvellement: { label: 'En renouvellement', color: 'bg-blue-100 text-blue-800' },
};

// Fonctions
const FONCTIONS = {
  dirigeant: 'Dirigeant',
  responsable_exploitation: 'Responsable exploitation',
  chef_culture: 'Chef de culture',
  tractoriste: 'Tractoriste',
  chauffeur_pl: 'Chauffeur PL',
  ouvrier_agricole: 'Ouvrier agricole',
  ouvrier_viticole: 'Ouvrier viticole',
  technicien: 'Technicien',
  mecanicien: 'Mécanicien',
  magasinier: 'Magasinier',
  administratif: 'Administratif',
  commercial: 'Commercial',
  autre: 'Autre',
};

// Types de documents
const TYPES_DOCUMENTS = {
  contrat_travail: { label: 'Contrat de travail', icon: FileText },
  rib: { label: 'RIB', icon: Euro },
  carte_identite: { label: 'Carte d\'identité', icon: User },
  permis_b: { label: 'Permis B', icon: Car },
  permis_c: { label: 'Permis C (PL)', icon: Car },
  certiphyto: { label: 'Certiphyto', icon: Leaf },
  caces: { label: 'CACES', icon: Wrench },
  visite_medicale: { label: 'Visite médicale', icon: User },
  attestation_formation: { label: 'Attestation formation', icon: GraduationCap },
  autre: { label: 'Autre', icon: FileText },
};

export default function CollaborateurDetailPage() {
  const { collaborateurId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const fileInputRef = useRef(null);
  const photoInputRef = useRef(null);

  // États
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [collaborateur, setCollaborateur] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [compteTtemps, setCompteTemps] = useState(null);
  const [pointages, setPointages] = useState([]);
  const [conges, setConges] = useState([]);
  const [soldeConges, setSoldeConges] = useState(null);
  const [absences, setAbsences] = useState([]);
  const [absencesStats, setAbsencesStats] = useState({});
  const [interventions, setInterventions] = useState([]);
  const [evaluations, setEvaluations] = useState([]);
  const [fichesPaie, setFichesPaie] = useState([]);
  const [materiels, setMateriels] = useState([]);

  // Formulaire d'édition
  const [formData, setFormData] = useState({});

  // Dialogs
  const [showDocDialog, setShowDocDialog] = useState(false);
  const [showCongeDialog, setShowCongeDialog] = useState(false);
  const [showEvalDialog, setShowEvalDialog] = useState(false);
  const [showPointageDialog, setShowPointageDialog] = useState(false);
  const [showAbsenceDialog, setShowAbsenceDialog] = useState(false);

  // Notes confidentielles (admin only)
  const [notesConfidentielles, setNotesConfidentielles] = useState('');
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesSaving, setNotesSaving] = useState(false);
  const [notesEditing, setNotesEditing] = useState(false);

  // Gestion compte utilisateur
  const [accountStatus, setAccountStatus] = useState(null);
  const [accountLoading, setAccountLoading] = useState(false);
  const [creatingAccount, setCreatingAccount] = useState(false);
  const [newCredentials, setNewCredentials] = useState(null);
  const [showCredentialsDialog, setShowCredentialsDialog] = useState(false);

  // Charger les données
  const loadData = useCallback(async () => {
    if (!collaborateurId) return;
    
    try {
      const res = await fetch(`${API_URL}/api/equipe/${collaborateurId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Collaborateur non trouvé');
      
      const data = await res.json();
      setCollaborateur(data.collaborateur);
      setFormData(data.collaborateur);
      setDocuments(data.documents || []);
      setCompteTemps(data.compte_temps);
      setPointages(data.historique_heures || []);
      setConges(data.conges || []);
      setSoldeConges(data.solde_conges);
      setInterventions(data.interventions || []);
      setEvaluations(data.evaluations || []);
      setFichesPaie(data.fiches_paie || []);
      setMateriels(data.materiels_autorises || []);
      
      // Charger les absences
      const absRes = await fetch(`${API_URL}/api/equipe/${collaborateurId}/absences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (absRes.ok) {
        const absData = await absRes.json();
        setAbsences(absData.absences || []);
        setAbsencesStats(absData.stats || {});
      }
    } catch (error) {
      toast.error('Erreur', { description: error.message });
    } finally {
      setLoading(false);
    }
  }, [collaborateurId, token]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Charger les notes confidentielles
  const loadNotesConfidentielles = useCallback(async () => {
    if (!collaborateurId) return;
    setNotesLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/equipe/${collaborateurId}/notes-confidentielles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setNotesConfidentielles(data.notes_confidentielles || '');
      }
    } catch (error) {
      // Silencieux si pas admin
    } finally {
      setNotesLoading(false);
    }
  }, [collaborateurId, token]);

  useEffect(() => {
    loadNotesConfidentielles();
  }, [loadNotesConfidentielles]);

  // Charger le statut du compte
  const loadAccountStatus = useCallback(async () => {
    if (!collaborateurId) return;
    setAccountLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/equipe/${collaborateurId}/statut-compte`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAccountStatus(data);
      }
    } catch (error) {
      console.error('Erreur chargement statut compte:', error);
    } finally {
      setAccountLoading(false);
    }
  }, [collaborateurId, token]);

  useEffect(() => {
    loadAccountStatus();
  }, [loadAccountStatus]);

  // Créer un accès utilisateur
  const handleCreateAccount = async () => {
    setCreatingAccount(true);
    try {
      const res = await fetch(`${API_URL}/api/equipe/${collaborateurId}/creer-acces`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Erreur création compte');
      }
      
      const data = await res.json();
      setNewCredentials(data.credentials);
      setShowCredentialsDialog(true);
      toast.success('Compte créé avec succès');
      loadAccountStatus();
    } catch (error) {
      toast.error('Erreur', { description: error.message });
    } finally {
      setCreatingAccount(false);
    }
  };

  // Régénérer le mot de passe
  const handleResetPassword = async () => {
    if (!confirm('Régénérer un nouveau mot de passe pour ce collaborateur ?')) return;
    
    setCreatingAccount(true);
    try {
      const res = await fetch(`${API_URL}/api/equipe/${collaborateurId}/regenerer-mot-de-passe`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Erreur');
      }
      
      const data = await res.json();
      setNewCredentials(data.credentials);
      setShowCredentialsDialog(true);
      toast.success('Mot de passe régénéré');
    } catch (error) {
      toast.error('Erreur', { description: error.message });
    } finally {
      setCreatingAccount(false);
    }
  };

  // Copier les identifiants
  const copyCredentials = () => {
    if (!newCredentials) return;
    const text = `URL: https://bureauagricole.fr\nEmail: ${newCredentials.email}\nMot de passe: ${newCredentials.password}`;
    navigator.clipboard.writeText(text);
    toast.success('Identifiants copiés !');
  };

  // Sauvegarder les notes confidentielles
  const handleSaveNotes = async () => {
    setNotesSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/equipe/${collaborateurId}/notes-confidentielles`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ notes_confidentielles: notesConfidentielles })
      });
      
      if (!res.ok) throw new Error('Erreur lors de la sauvegarde');
      
      toast.success('Notes sauvegardées');
      setNotesEditing(false);
    } catch (error) {
      toast.error('Erreur', { description: error.message });
    } finally {
      setNotesSaving(false);
    }
  };

  // Sauvegarder les modifications
  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/equipe/${collaborateurId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (!res.ok) throw new Error('Erreur lors de la sauvegarde');
      
      toast.success('Modifications enregistrées');
      setEditMode(false);
      loadData();
    } catch (error) {
      toast.error('Erreur', { description: error.message });
    } finally {
      setSaving(false);
    }
  };

  // Upload photo
  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await fetch(`${API_URL}/api/equipe/${collaborateurId}/photo`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (!res.ok) throw new Error('Erreur upload photo');
      
      toast.success('Photo mise à jour');
      loadData();
    } catch (error) {
      toast.error('Erreur', { description: error.message });
    }
  };

  // Upload document
  const handleDocUpload = async (e, typeDoc) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('type_document', typeDoc);

    try {
      const res = await fetch(`${API_URL}/api/equipe/${collaborateurId}/documents`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (!res.ok) throw new Error('Erreur upload document');
      
      toast.success('Document ajouté');
      setShowDocDialog(false);
      loadData();
    } catch (error) {
      toast.error('Erreur', { description: error.message });
    }
  };

  // Supprimer document
  const handleDeleteDoc = async (docId) => {
    if (!confirm('Supprimer ce document ?')) return;

    try {
      await fetch(`${API_URL}/api/equipe/${collaborateurId}/documents/${docId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Document supprimé');
      loadData();
    } catch (error) {
      toast.error('Erreur', { description: error.message });
    }
  };

  // Demander congé
  const handleDemandeConge = async (congeData) => {
    try {
      const res = await fetch(`${API_URL}/api/equipe/${collaborateurId}/conges`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(congeData)
      });

      if (!res.ok) throw new Error('Erreur demande congé');
      
      toast.success('Demande de congé enregistrée');
      setShowCongeDialog(false);
      loadData();
    } catch (error) {
      toast.error('Erreur', { description: error.message });
    }
  };

  // Ajouter pointage
  const handleAddPointage = async (pointageData) => {
    try {
      const res = await fetch(`${API_URL}/api/equipe/${collaborateurId}/heures`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(pointageData)
      });

      if (!res.ok) throw new Error('Erreur pointage');
      
      toast.success('Heures enregistrées');
      setShowPointageDialog(false);
      loadData();
    } catch (error) {
      toast.error('Erreur', { description: error.message });
    }
  };

  // Ajouter évaluation
  const handleAddEvaluation = async (evalData) => {
    try {
      const res = await fetch(`${API_URL}/api/equipe/${collaborateurId}/evaluations`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(evalData)
      });

      if (!res.ok) throw new Error('Erreur évaluation');
      
      toast.success('Évaluation enregistrée');
      setShowEvalDialog(false);
      loadData();
    } catch (error) {
      toast.error('Erreur', { description: error.message });
    }
  };

  // Ajouter absence
  const handleAddAbsence = async (absenceData) => {
    try {
      const res = await fetch(`${API_URL}/api/equipe/${collaborateurId}/absences`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(absenceData)
      });

      if (!res.ok) throw new Error('Erreur absence');
      
      toast.success('Absence enregistrée');
      setShowAbsenceDialog(false);
      loadData();
    } catch (error) {
      toast.error('Erreur', { description: error.message });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!collaborateur) {
    return (
      <div className="p-6">
        <Button variant="ghost" onClick={() => navigate('/team')}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Retour
        </Button>
        <p className="text-center mt-8 text-muted-foreground">Collaborateur non trouvé</p>
      </div>
    );
  }

  const initials = `${collaborateur.prenom?.[0] || ''}${collaborateur.nom?.[0] || ''}`.toUpperCase();
  const heuresRealisees = compteTtemps?.heures_realisees || 0;
  const heuresContrat = compteTtemps?.heures_contrat || collaborateur.heures_contrat_annuel || 1607;
  const progressHeures = Math.min((heuresRealisees / heuresContrat) * 100, 100);

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-7xl mx-auto">
      {/* Header avec retour */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/team')}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Équipe
        </Button>
      </div>

      {/* Carte profil principale */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row gap-6">
            {/* Photo et infos de base */}
            <div className="flex flex-col items-center gap-4">
              <div className="relative group">
                <Avatar className="w-32 h-32">
                  <AvatarImage src={collaborateur.photo_url ? `${API_URL}${collaborateur.photo_url}` : undefined} />
                  <AvatarFallback className="text-3xl bg-primary/10">{initials}</AvatarFallback>
                </Avatar>
                <button
                  onClick={() => photoInputRef.current?.click()}
                  className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-full"
                >
                  <Camera className="w-8 h-8 text-white" />
                </button>
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handlePhotoUpload}
                />
              </div>
              
              <div className="text-center">
                <h1 className="text-2xl font-bold">{collaborateur.prenom} {collaborateur.nom}</h1>
                <p className="text-muted-foreground">{FONCTIONS[collaborateur.fonction] || collaborateur.role}</p>
                <Badge variant={collaborateur.is_active !== false ? 'default' : 'secondary'} className="mt-2">
                  {collaborateur.is_active !== false ? 'Actif' : 'Inactif'}
                </Badge>
              </div>
            </div>

            {/* Informations détaillées */}
            <div className="flex-1 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Contact */}
              <div className="space-y-2">
                <h3 className="font-semibold flex items-center gap-2">
                  <Mail className="w-4 h-4" /> Contact
                </h3>
                {editMode ? (
                  <div className="space-y-2">
                    <div>
                      <Label className="text-xs">Email</Label>
                      <Input
                        type="email"
                        value={formData.email || ''}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        placeholder="email@example.com"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Téléphone</Label>
                      <Input
                        value={formData.telephone || ''}
                        onChange={(e) => setFormData({...formData, telephone: e.target.value})}
                        placeholder="06 12 34 56 78"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Adresse</Label>
                      <Input
                        value={formData.adresse || ''}
                        onChange={(e) => setFormData({...formData, adresse: e.target.value})}
                        placeholder="123 rue..."
                        className="mt-1"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Code postal</Label>
                        <Input
                          value={formData.code_postal || ''}
                          onChange={(e) => setFormData({...formData, code_postal: e.target.value})}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Ville</Label>
                        <Input
                          value={formData.ville || ''}
                          onChange={(e) => setFormData({...formData, ville: e.target.value})}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <p className="text-sm">{collaborateur.email}</p>
                    {collaborateur.telephone && (
                      <p className="text-sm flex items-center gap-2">
                        <Phone className="w-3 h-3" /> {collaborateur.telephone}
                      </p>
                    )}
                    {collaborateur.adresse && (
                      <p className="text-sm flex items-center gap-2 text-muted-foreground">
                        <MapPin className="w-3 h-3" /> {collaborateur.adresse}, {collaborateur.code_postal} {collaborateur.ville}
                      </p>
                    )}
                  </>
                )}
              </div>

              {/* Contrat */}
              <div className="space-y-2">
                <h3 className="font-semibold flex items-center gap-2">
                  <Briefcase className="w-4 h-4" /> Contrat
                </h3>
                <div className="flex items-center gap-2">
                  <p className="text-sm">{TYPES_CONTRAT[collaborateur.type_contrat] || 'Non défini'}</p>
                  {collaborateur.statut_contrat && (
                    <Badge className={STATUTS_CONTRAT[collaborateur.statut_contrat]?.color || 'bg-gray-100'}>
                      {STATUTS_CONTRAT[collaborateur.statut_contrat]?.label || collaborateur.statut_contrat}
                    </Badge>
                  )}
                </div>
                {collaborateur.date_entree && (
                  <p className="text-sm text-muted-foreground">
                    Depuis le {new Date(collaborateur.date_entree).toLocaleDateString('fr-FR')}
                  </p>
                )}
                {collaborateur.salaire_brut_mensuel && (
                  <p className="text-sm">{collaborateur.salaire_brut_mensuel}€ brut/mois</p>
                )}
              </div>

              {/* Certifications */}
              <div className="space-y-2">
                <h3 className="font-semibold flex items-center gap-2">
                  <Shield className="w-4 h-4" /> Certifications
                </h3>
                {collaborateur.certiphyto_numero ? (
                  <div className="text-sm">
                    <Badge variant="outline" className="gap-1">
                      <Leaf className="w-3 h-3" /> Certiphyto
                    </Badge>
                    {collaborateur.certiphyto_date_expiration && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Expire: {collaborateur.certiphyto_date_expiration}
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Aucun certiphyto</p>
                )}
                {collaborateur.permis_categories?.length > 0 && (
                  <div className="flex gap-1 flex-wrap mt-2">
                    {collaborateur.permis_categories.map(cat => (
                      <Badge key={cat} variant="secondary" className="text-xs">{cat}</Badge>
                    ))}
                  </div>
                )}
              </div>

              {/* Heures */}
              <div className="space-y-2">
                <h3 className="font-semibold flex items-center gap-2">
                  <Clock className="w-4 h-4" /> Heures {new Date().getFullYear()}
                </h3>
                <div className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span>Réalisées</span>
                    <span className="font-medium">{heuresRealisees}h / {heuresContrat}h</span>
                  </div>
                  <Progress value={progressHeures} className="h-2" />
                  <p className="text-xs text-muted-foreground">
                    Solde: {compteTtemps?.solde || 0}h
                  </p>
                </div>
              </div>

              {/* Congés */}
              <div className="space-y-2">
                <h3 className="font-semibold flex items-center gap-2">
                  <CalendarDays className="w-4 h-4" /> Congés
                </h3>
                <div className="text-sm">
                  <p>CP: {soldeConges?.cp_solde || 25} jours restants</p>
                  <p className="text-xs text-muted-foreground">
                    Pris: {soldeConges?.cp_pris || 0} / Acquis: {soldeConges?.cp_acquis || 25}
                  </p>
                </div>
              </div>

              {/* Évaluation */}
              <div className="space-y-2">
                <h3 className="font-semibold flex items-center gap-2">
                  <Star className="w-4 h-4" /> Évaluation
                </h3>
                {collaborateur.note_globale ? (
                  <div className="flex items-center gap-2">
                    <div className="flex">
                      {[1,2,3,4,5].map(n => (
                        <Star
                          key={n}
                          className={`w-4 h-4 ${n <= collaborateur.note_globale ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
                        />
                      ))}
                    </div>
                    <span className="text-sm">{collaborateur.note_globale}/5</span>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Non évalué</p>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-2">
              <Button 
                onClick={() => {
                  if (editMode) {
                    handleSave();
                  } else {
                    setEditMode(true);
                  }
                }} 
                variant={editMode ? 'default' : 'outline'}
                disabled={saving}
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : editMode ? (
                  <Save className="w-4 h-4 mr-2" />
                ) : (
                  <Edit className="w-4 h-4 mr-2" />
                )}
                {editMode ? 'Sauvegarder' : 'Modifier'}
              </Button>
              {editMode && (
                <Button variant="ghost" onClick={() => { setEditMode(false); setFormData(collaborateur); }}>
                  <X className="w-4 h-4 mr-2" /> Annuler
                </Button>
              )}
            </div>
          </div>

          {/* Alertes */}
          {collaborateur.alertes?.length > 0 && (
            <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
              <div className="flex items-center gap-2 text-yellow-700 dark:text-yellow-400">
                <AlertTriangle className="w-4 h-4" />
                <span className="font-medium">Alertes</span>
              </div>
              <ul className="mt-2 space-y-1">
                {collaborateur.alertes.map((a, i) => (
                  <li key={i} className="text-sm text-yellow-600 dark:text-yellow-300">{a.message}</li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Notes Confidentielles (Admin only) */}
      <Card className="border-amber-200 bg-amber-50/30">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <StickyNote className="w-4 h-4 text-amber-600" />
            Notes confidentielles
            <Lock className="w-3 h-3 text-muted-foreground" />
            <span className="text-xs font-normal text-muted-foreground ml-2">(visible uniquement par les administrateurs)</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {notesLoading ? (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-amber-600"></div>
            </div>
          ) : notesEditing ? (
            <div className="space-y-3">
              <Textarea
                value={notesConfidentielles}
                onChange={(e) => setNotesConfidentielles(e.target.value)}
                placeholder="Ajoutez vos notes personnelles sur ce collaborateur (pense-bêtes, remarques, observations...)&#10;Ces notes sont strictement confidentielles et ne sont jamais visibles par le salarié."
                className="min-h-[120px] bg-white"
              />
              <div className="flex justify-end gap-2">
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => {
                    setNotesEditing(false);
                    loadNotesConfidentielles();
                  }}
                >
                  <X className="w-4 h-4 mr-1" /> Annuler
                </Button>
                <Button 
                  size="sm"
                  onClick={handleSaveNotes}
                  disabled={notesSaving}
                  className="bg-amber-600 hover:bg-amber-700"
                >
                  {notesSaving ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  ) : (
                    <Save className="w-4 h-4 mr-1" />
                  )}
                  Sauvegarder
                </Button>
              </div>
            </div>
          ) : (
            <div 
              className="cursor-pointer group"
              onClick={() => setNotesEditing(true)}
            >
              {notesConfidentielles ? (
                <div className="p-3 bg-white rounded border border-amber-100 group-hover:border-amber-300 transition-colors">
                  <p className="text-sm whitespace-pre-wrap">{notesConfidentielles}</p>
                  <p className="text-xs text-muted-foreground mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    Cliquez pour modifier
                  </p>
                </div>
              ) : (
                <div className="p-3 bg-white rounded border border-dashed border-amber-200 group-hover:border-amber-400 transition-colors text-center">
                  <p className="text-sm text-muted-foreground">
                    <Plus className="w-4 h-4 inline mr-1" />
                    Cliquez pour ajouter une note confidentielle
                  </p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Gestion Accès Utilisateur */}
      <Card className="border-blue-200 bg-blue-50/30">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Lock className="w-4 h-4 text-blue-600" />
            Accès utilisateur
            <span className="text-xs font-normal text-muted-foreground ml-2">(connexion à l'application)</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {accountLoading ? (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
            </div>
          ) : accountStatus?.has_account ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-green-700">
                <CheckCircle className="w-5 h-5" />
                <span className="font-medium">Compte actif</span>
              </div>
              <div className="text-sm text-muted-foreground space-y-1">
                <p>Email : {collaborateur.email}</p>
                {accountStatus.account_created_at && (
                  <p>Créé le : {new Date(accountStatus.account_created_at).toLocaleDateString('fr-FR')}</p>
                )}
                {accountStatus.user_info?.derniere_connexion && (
                  <p>Dernière connexion : {new Date(accountStatus.user_info.derniere_connexion).toLocaleString('fr-FR')}</p>
                )}
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleResetPassword}
                disabled={creatingAccount}
              >
                {creatingAccount ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mr-2"></div>
                ) : (
                  <Lock className="w-4 h-4 mr-2" />
                )}
                Régénérer le mot de passe
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Ce collaborateur n'a pas encore de compte pour se connecter à l'application.
              </p>
              <Button 
                onClick={handleCreateAccount}
                disabled={creatingAccount || !collaborateur.email}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {creatingAccount ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                ) : (
                  <UserPlus className="w-4 h-4 mr-2" />
                )}
                Créer un accès
              </Button>
              {!collaborateur.email && (
                <p className="text-xs text-red-500">Email requis pour créer un compte</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog identifiants */}
      <Dialog open={showCredentialsDialog} onOpenChange={setShowCredentialsDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              Identifiants de connexion
            </DialogTitle>
          </DialogHeader>
          {newCredentials && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Transmettez ces identifiants à {collaborateur.prenom} {collaborateur.nom} :
              </p>
              <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4 space-y-2 font-mono text-sm">
                <p><strong>Site :</strong> https://bureauagricole.fr</p>
                <p><strong>Email :</strong> {newCredentials.email}</p>
                <p><strong>Mot de passe :</strong> {newCredentials.password}</p>
              </div>
              <Button onClick={copyCredentials} className="w-full">
                <Copy className="w-4 h-4 mr-2" />
                Copier les identifiants
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Onglets détaillés */}
      <Tabs defaultValue="documents" className="w-full">
        <TabsList className="grid w-full grid-cols-4 md:grid-cols-8 h-auto">
          <TabsTrigger value="documents" className="gap-2">
            <FileText className="w-4 h-4" /> Documents
          </TabsTrigger>
          <TabsTrigger value="heures" className="gap-2">
            <Clock className="w-4 h-4" /> Heures
          </TabsTrigger>
          <TabsTrigger value="absences" className="gap-2">
            <AlertTriangle className="w-4 h-4" /> Absences
          </TabsTrigger>
          <TabsTrigger value="conges" className="gap-2">
            <CalendarDays className="w-4 h-4" /> Congés
          </TabsTrigger>
          <TabsTrigger value="interventions" className="gap-2">
            <Tractor className="w-4 h-4" /> Interventions
          </TabsTrigger>
          <TabsTrigger value="evaluations" className="gap-2">
            <Star className="w-4 h-4" /> Évaluation
          </TabsTrigger>
          <TabsTrigger value="paie" className="gap-2">
            <Euro className="w-4 h-4" /> Paie
          </TabsTrigger>
          <TabsTrigger value="acces" className="gap-2">
            <Shield className="w-4 h-4" /> Accès
          </TabsTrigger>
        </TabsList>

        {/* Documents */}
        <TabsContent value="documents">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Documents ({documents.length})</CardTitle>
              <Button onClick={() => setShowDocDialog(true)}>
                <Plus className="w-4 h-4 mr-2" /> Ajouter
              </Button>
            </CardHeader>
            <CardContent>
              {documents.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground">Aucun document</p>
              ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {documents.map(doc => {
                    const TypeIcon = TYPES_DOCUMENTS[doc.type_document]?.icon || FileText;
                    return (
                      <Card key={doc.id} className={doc.expire ? 'border-red-500' : doc.expire_bientot ? 'border-yellow-500' : ''}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3">
                              <TypeIcon className="w-8 h-8 text-muted-foreground" />
                              <div>
                                <p className="font-medium">{doc.type_label || doc.type_document}</p>
                                <p className="text-xs text-muted-foreground">{doc.nom_fichier}</p>
                                {doc.date_expiration && (
                                  <p className={`text-xs ${doc.expire ? 'text-red-500' : doc.expire_bientot ? 'text-yellow-500' : 'text-muted-foreground'}`}>
                                    {doc.expire ? 'Expiré' : doc.expire_bientot ? 'Expire bientôt' : `Expire: ${doc.date_expiration}`}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="flex gap-1">
                              <Button size="icon" variant="ghost" asChild>
                                <a href={`${API_URL}${doc.fichier_url}`} target="_blank" rel="noopener noreferrer">
                                  <Download className="w-4 h-4" />
                                </a>
                              </Button>
                              <Button size="icon" variant="ghost" onClick={() => handleDeleteDoc(doc.id)}>
                                <Trash2 className="w-4 h-4 text-destructive" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Heures */}
        <TabsContent value="heures">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Suivi des heures</CardTitle>
                <CardDescription>Compte temps lissé annuel</CardDescription>
              </div>
              <Button onClick={() => setShowPointageDialog(true)}>
                <Plus className="w-4 h-4 mr-2" /> Pointer
              </Button>
            </CardHeader>
            <CardContent>
              {/* Résumé */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="p-4 rounded-lg bg-muted">
                  <p className="text-sm text-muted-foreground">Contrat annuel</p>
                  <p className="text-2xl font-bold">{heuresContrat}h</p>
                </div>
                <div className="p-4 rounded-lg bg-muted">
                  <p className="text-sm text-muted-foreground">Réalisées</p>
                  <p className="text-2xl font-bold">{heuresRealisees}h</p>
                </div>
                <div className="p-4 rounded-lg bg-muted">
                  <p className="text-sm text-muted-foreground">Payées</p>
                  <p className="text-2xl font-bold">{compteTtemps?.heures_payees || 0}h</p>
                </div>
                <div className="p-4 rounded-lg bg-primary/10">
                  <p className="text-sm text-muted-foreground">Solde</p>
                  <p className="text-2xl font-bold">{compteTtemps?.solde || 0}h</p>
                </div>
              </div>

              {/* Historique */}
              <ScrollArea className="h-[400px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Heures</TableHead>
                      <TableHead>Sup.</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Commentaire</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pointages.slice(0, 50).map((p, i) => (
                      <TableRow key={i}>
                        <TableCell>{new Date(p.date).toLocaleDateString('fr-FR')}</TableCell>
                        <TableCell>{p.heures_travaillees}h</TableCell>
                        <TableCell>{p.heures_supplementaires || 0}h</TableCell>
                        <TableCell>
                          <Badge variant="outline">{p.type_heures || 'Normal'}</Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">{p.commentaire}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Absences */}
        <TabsContent value="absences">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Absences (maladie, retard, etc.)</CardTitle>
                <CardDescription>Suivi des absences et incidents</CardDescription>
              </div>
              <Button onClick={() => setShowAbsenceDialog(true)}>
                <Plus className="w-4 h-4 mr-2" /> Déclarer
              </Button>
            </CardHeader>
            <CardContent>
              {/* Stats par type */}
              {Object.keys(absencesStats).length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  {Object.entries(absencesStats).map(([type, stats]) => (
                    <div key={type} className="p-3 rounded-lg bg-muted">
                      <p className="text-xs text-muted-foreground capitalize">{type.replace('_', ' ')}</p>
                      <p className="text-lg font-bold">{stats.count} fois</p>
                      <p className="text-xs">{stats.jours} jours / {stats.heures}h</p>
                    </div>
                  ))}
                </div>
              )}

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Durée</TableHead>
                    <TableHead>Justificatif</TableHead>
                    <TableHead>Motif</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {absences.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                        Aucune absence enregistrée
                      </TableCell>
                    </TableRow>
                  ) : (
                    absences.map((abs, i) => (
                      <TableRow key={i}>
                        <TableCell>
                          {new Date(abs.date_debut).toLocaleDateString('fr-FR')}
                          {abs.date_fin && abs.date_fin !== abs.date_debut && (
                            <> - {new Date(abs.date_fin).toLocaleDateString('fr-FR')}</>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant={
                            abs.type_absence === 'maladie' ? 'secondary' :
                            abs.type_absence === 'absence_injustifiee' ? 'destructive' :
                            abs.type_absence === 'retard' ? 'outline' :
                            'default'
                          }>
                            {abs.type_label || abs.type_absence}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {abs.heures ? `${abs.heures}h` : `${abs.nb_jours || 1} jour(s)`}
                        </TableCell>
                        <TableCell>
                          {abs.justificatif_requis ? (
                            abs.justificatif_fourni ? (
                              <Badge variant="default" className="bg-green-500">Fourni</Badge>
                            ) : (
                              <Badge variant="destructive">Manquant</Badge>
                            )
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-muted-foreground">{abs.motif || '-'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Congés */}
        <TabsContent value="conges">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Congés et absences</CardTitle>
                <CardDescription>Solde: {soldeConges?.cp_solde || 25} CP restants</CardDescription>
              </div>
              <Button onClick={() => setShowCongeDialog(true)}>
                <Plus className="w-4 h-4 mr-2" /> Demander
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Période</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Jours</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Motif</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {conges.map((c, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        {new Date(c.date_debut).toLocaleDateString('fr-FR')} - {new Date(c.date_fin).toLocaleDateString('fr-FR')}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{c.type_conge?.toUpperCase() || 'CP'}</Badge>
                      </TableCell>
                      <TableCell>{c.nb_jours}</TableCell>
                      <TableCell>
                        <Badge variant={
                          c.statut === 'valide' ? 'default' :
                          c.statut === 'refuse' ? 'destructive' :
                          'secondary'
                        }>
                          {c.statut}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{c.motif}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Interventions */}
        <TabsContent value="interventions">
          <Card>
            <CardHeader>
              <CardTitle>Interventions réalisées ({interventions.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Opération</TableHead>
                      <TableHead>Parcelle</TableHead>
                      <TableHead>Durée</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {interventions.map((inter, i) => (
                      <TableRow key={i}>
                        <TableCell>{new Date(inter.date).toLocaleDateString('fr-FR')}</TableCell>
                        <TableCell>{inter.operation}</TableCell>
                        <TableCell>{inter.parcelle_nom || '-'}</TableCell>
                        <TableCell>{inter.duree_heures || 0}h</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Évaluations */}
        <TabsContent value="evaluations">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Évaluations</CardTitle>
              <Button onClick={() => setShowEvalDialog(true)}>
                <Plus className="w-4 h-4 mr-2" /> Nouvelle évaluation
              </Button>
            </CardHeader>
            <CardContent>
              {/* Points forts / Points à améliorer */}
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="p-4 rounded-lg bg-green-50 dark:bg-green-900/20">
                  <h4 className="font-medium flex items-center gap-2 text-green-700 dark:text-green-400 mb-3">
                    <TrendingUp className="w-4 h-4" /> Points forts
                  </h4>
                  <ul className="space-y-2">
                    {(collaborateur.points_forts || []).map((p, i) => (
                      <li key={i} className="text-sm flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" /> {p}
                      </li>
                    ))}
                    {(!collaborateur.points_forts || collaborateur.points_forts.length === 0) && (
                      <li className="text-sm text-muted-foreground">Non renseigné</li>
                    )}
                  </ul>
                </div>
                <div className="p-4 rounded-lg bg-orange-50 dark:bg-orange-900/20">
                  <h4 className="font-medium flex items-center gap-2 text-orange-700 dark:text-orange-400 mb-3">
                    <TrendingDown className="w-4 h-4" /> Points à améliorer
                  </h4>
                  <ul className="space-y-2">
                    {(collaborateur.points_amelioration || []).map((p, i) => (
                      <li key={i} className="text-sm flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-orange-500" /> {p}
                      </li>
                    ))}
                    {(!collaborateur.points_amelioration || collaborateur.points_amelioration.length === 0) && (
                      <li className="text-sm text-muted-foreground">Non renseigné</li>
                    )}
                  </ul>
                </div>
              </div>

              {/* Historique évaluations */}
              <h4 className="font-medium mb-3">Historique des évaluations</h4>
              {evaluations.length === 0 ? (
                <p className="text-center py-4 text-muted-foreground">Aucune évaluation</p>
              ) : (
                <div className="space-y-4">
                  {evaluations.map((ev, i) => (
                    <Card key={i}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{new Date(ev.date).toLocaleDateString('fr-FR')}</span>
                            <div className="flex">
                              {[1,2,3,4,5].map(n => (
                                <Star
                                  key={n}
                                  className={`w-4 h-4 ${n <= ev.note_globale ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
                                />
                              ))}
                            </div>
                          </div>
                          <span className="text-sm text-muted-foreground">Par {ev.evaluateur_nom}</span>
                        </div>
                        <p className="text-sm">{ev.commentaire}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Fiches de paie */}
        <TabsContent value="paie">
          <Card>
            <CardHeader>
              <CardTitle>Fiches de paie</CardTitle>
            </CardHeader>
            <CardContent>
              {fichesPaie.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground">Aucune fiche de paie</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Période</TableHead>
                      <TableHead>Net à payer</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {fichesPaie.map((f, i) => (
                      <TableRow key={i}>
                        <TableCell>{f.mois}/{f.annee}</TableCell>
                        <TableCell className="font-medium">{f.net_a_payer?.toFixed(2)}€</TableCell>
                        <TableCell>
                          <Badge variant={f.statut === 'paye' ? 'default' : 'secondary'}>{f.statut}</Badge>
                        </TableCell>
                        <TableCell>
                          <Button size="sm" variant="ghost">
                            <Download className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Accès & Droits */}
        <TabsContent value="acces">
          <AccesCollaborateurPanel collaborateurId={collaborateurId} />
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      
      {/* Dialog ajout document */}
      <Dialog open={showDocDialog} onOpenChange={setShowDocDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajouter un document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(TYPES_DOCUMENTS).map(([key, { label, icon: Icon }]) => (
                <Button
                  key={key}
                  variant="outline"
                  className="justify-start h-auto py-3"
                  onClick={() => {
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = '.pdf,.jpg,.jpeg,.png,.doc,.docx';
                    input.onchange = (e) => handleDocUpload(e, key);
                    input.click();
                  }}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {label}
                </Button>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog demande congé */}
      <DemandeCongeDialog
        open={showCongeDialog}
        onOpenChange={setShowCongeDialog}
        onSubmit={handleDemandeConge}
      />

      {/* Dialog pointage */}
      <PointageDialog
        open={showPointageDialog}
        onOpenChange={setShowPointageDialog}
        onSubmit={handleAddPointage}
      />

      {/* Dialog évaluation */}
      <EvaluationDialog
        open={showEvalDialog}
        onOpenChange={setShowEvalDialog}
        onSubmit={handleAddEvaluation}
      />

      {/* Dialog absence */}
      <AbsenceDialog
        open={showAbsenceDialog}
        onOpenChange={setShowAbsenceDialog}
        onSubmit={handleAddAbsence}
      />
    </div>
  );
}

// Composant dialog demande de congé
function DemandeCongeDialog({ open, onOpenChange, onSubmit }) {
  const [form, setForm] = useState({
    date_debut: '',
    date_fin: '',
    type_conge: 'cp',
    motif: '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(form);
    setForm({ date_debut: '', date_fin: '', type_conge: 'cp', motif: '' });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Demande de congé</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Date début</Label>
              <Input
                type="date"
                value={form.date_debut}
                onChange={e => setForm({ ...form, date_debut: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Date fin</Label>
              <Input
                type="date"
                value={form.date_fin}
                onChange={e => setForm({ ...form, date_fin: e.target.value })}
                required
              />
            </div>
          </div>
          <div>
            <Label>Type</Label>
            <Select value={form.type_conge} onValueChange={v => setForm({ ...form, type_conge: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="cp">Congés payés</SelectItem>
                <SelectItem value="rtt">RTT</SelectItem>
                <SelectItem value="sans_solde">Sans solde</SelectItem>
                <SelectItem value="formation">Formation</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Motif (optionnel)</Label>
            <Textarea
              value={form.motif}
              onChange={e => setForm({ ...form, motif: e.target.value })}
              placeholder="Raison de la demande..."
            />
          </div>
          <DialogFooter>
            <Button type="submit">Envoyer la demande</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Composant dialog pointage heures
function PointageDialog({ open, onOpenChange, onSubmit }) {
  const [form, setForm] = useState({
    date: new Date().toISOString().split('T')[0],
    heures_travaillees: 8,
    heures_supplementaires: 0,
    type_heures: 'normal',
    commentaire: '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(form);
    setForm({
      date: new Date().toISOString().split('T')[0],
      heures_travaillees: 8,
      heures_supplementaires: 0,
      type_heures: 'normal',
      commentaire: '',
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Pointer des heures</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Date</Label>
            <Input
              type="date"
              value={form.date}
              onChange={e => setForm({ ...form, date: e.target.value })}
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Heures travaillées</Label>
              <Input
                type="number"
                step="0.5"
                min="0"
                max="24"
                value={form.heures_travaillees}
                onChange={e => setForm({ ...form, heures_travaillees: parseFloat(e.target.value) })}
                required
              />
            </div>
            <div>
              <Label>Heures sup.</Label>
              <Input
                type="number"
                step="0.5"
                min="0"
                max="24"
                value={form.heures_supplementaires}
                onChange={e => setForm({ ...form, heures_supplementaires: parseFloat(e.target.value) })}
              />
            </div>
          </div>
          <div>
            <Label>Type</Label>
            <Select value={form.type_heures} onValueChange={v => setForm({ ...form, type_heures: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="normal">Normal</SelectItem>
                <SelectItem value="nuit">Nuit</SelectItem>
                <SelectItem value="dimanche">Dimanche</SelectItem>
                <SelectItem value="ferie">Jour férié</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Commentaire</Label>
            <Input
              value={form.commentaire}
              onChange={e => setForm({ ...form, commentaire: e.target.value })}
              placeholder="Chantier, intervention..."
            />
          </div>
          <DialogFooter>
            <Button type="submit">Enregistrer</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Composant dialog évaluation
function EvaluationDialog({ open, onOpenChange, onSubmit }) {
  const [form, setForm] = useState({
    date: new Date().toISOString().split('T')[0],
    note_globale: 3,
    points_forts: [],
    points_amelioration: [],
    commentaire: '',
  });
  const [newPointFort, setNewPointFort] = useState('');
  const [newPointAmelio, setNewPointAmelio] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(form);
    setForm({
      date: new Date().toISOString().split('T')[0],
      note_globale: 3,
      points_forts: [],
      points_amelioration: [],
      commentaire: '',
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Nouvelle évaluation</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Date</Label>
              <Input
                type="date"
                value={form.date}
                onChange={e => setForm({ ...form, date: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Note globale</Label>
              <Select value={form.note_globale.toString()} onValueChange={v => setForm({ ...form, note_globale: parseInt(v) })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[1,2,3,4,5].map(n => (
                    <SelectItem key={n} value={n.toString()}>{n}/5</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>Points forts</Label>
            <div className="flex gap-2 mb-2">
              <Input
                value={newPointFort}
                onChange={e => setNewPointFort(e.target.value)}
                placeholder="Ajouter un point fort..."
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  if (newPointFort.trim()) {
                    setForm({ ...form, points_forts: [...form.points_forts, newPointFort.trim()] });
                    setNewPointFort('');
                  }
                }}
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {form.points_forts.map((p, i) => (
                <Badge key={i} variant="secondary" className="gap-1">
                  {p}
                  <X
                    className="w-3 h-3 cursor-pointer"
                    onClick={() => setForm({ ...form, points_forts: form.points_forts.filter((_, j) => j !== i) })}
                  />
                </Badge>
              ))}
            </div>
          </div>

          <div>
            <Label>Points à améliorer</Label>
            <div className="flex gap-2 mb-2">
              <Input
                value={newPointAmelio}
                onChange={e => setNewPointAmelio(e.target.value)}
                placeholder="Ajouter un point à améliorer..."
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  if (newPointAmelio.trim()) {
                    setForm({ ...form, points_amelioration: [...form.points_amelioration, newPointAmelio.trim()] });
                    setNewPointAmelio('');
                  }
                }}
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {form.points_amelioration.map((p, i) => (
                <Badge key={i} variant="outline" className="gap-1">
                  {p}
                  <X
                    className="w-3 h-3 cursor-pointer"
                    onClick={() => setForm({ ...form, points_amelioration: form.points_amelioration.filter((_, j) => j !== i) })}
                  />
                </Badge>
              ))}
            </div>
          </div>

          <div>
            <Label>Commentaire</Label>
            <Textarea
              value={form.commentaire}
              onChange={e => setForm({ ...form, commentaire: e.target.value })}
              placeholder="Observations, objectifs..."
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button type="submit">Enregistrer l'évaluation</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Composant dialog absence
function AbsenceDialog({ open, onOpenChange, onSubmit }) {
  const [form, setForm] = useState({
    type_absence: 'maladie',
    date_debut: new Date().toISOString().split('T')[0],
    date_fin: '',
    heures: '',
    motif: '',
    justificatif_fourni: false,
  });

  const TYPES_ABSENCES = {
    maladie: 'Arrêt maladie',
    accident_travail: 'Accident du travail',
    retard: 'Retard',
    absence_injustifiee: 'Absence injustifiée',
    absence_autorisee: 'Absence autorisée',
    enfant_malade: 'Enfant malade',
    deces_famille: 'Décès famille',
    mariage: 'Mariage/PACS',
    demenagement: 'Déménagement',
    convocation_officielle: 'Convocation officielle',
    intemperies: 'Intempéries',
    formation: 'Formation',
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = {
      ...form,
      heures: form.heures ? parseFloat(form.heures) : null,
    };
    onSubmit(data);
    setForm({
      type_absence: 'maladie',
      date_debut: new Date().toISOString().split('T')[0],
      date_fin: '',
      heures: '',
      motif: '',
      justificatif_fourni: false,
    });
  };

  const isRetard = form.type_absence === 'retard';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Déclarer une absence</DialogTitle>
          <DialogDescription>
            Enregistrez une absence, maladie, retard ou autre incident
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Type d'absence</Label>
            <Select value={form.type_absence} onValueChange={v => setForm({ ...form, type_absence: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(TYPES_ABSENCES).map(([key, label]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Date {isRetard ? '' : 'début'}</Label>
              <Input
                type="date"
                value={form.date_debut}
                onChange={e => setForm({ ...form, date_debut: e.target.value })}
                required
              />
            </div>
            {!isRetard && (
              <div>
                <Label>Date fin (optionnel)</Label>
                <Input
                  type="date"
                  value={form.date_fin}
                  onChange={e => setForm({ ...form, date_fin: e.target.value })}
                />
              </div>
            )}
            {isRetard && (
              <div>
                <Label>Durée (heures)</Label>
                <Input
                  type="number"
                  step="0.5"
                  min="0"
                  max="8"
                  value={form.heures}
                  onChange={e => setForm({ ...form, heures: e.target.value })}
                  placeholder="Ex: 1.5"
                />
              </div>
            )}
          </div>

          <div>
            <Label>Motif (optionnel)</Label>
            <Textarea
              value={form.motif}
              onChange={e => setForm({ ...form, motif: e.target.value })}
              placeholder="Raison de l'absence..."
              rows={2}
            />
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="justificatif"
              checked={form.justificatif_fourni}
              onChange={e => setForm({ ...form, justificatif_fourni: e.target.checked })}
              className="rounded border-gray-300"
            />
            <Label htmlFor="justificatif" className="text-sm font-normal">
              Justificatif fourni (arrêt maladie, certificat, etc.)
            </Label>
          </div>

          <DialogFooter>
            <Button type="submit">Enregistrer l'absence</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
